import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useLanguage } from "../lib/language";
import { Instagram, Facebook, MessageCircle, ExternalLink, BadgeCheck, ArrowLeft } from "lucide-react";
import ProductCard from "../components/ProductCard";

export default function VendorPublic() {
    const { t } = useLanguage();
    const { vid } = useParams();
    const [state, setState] = useState({ loading: true, data: null, error: null });

    useEffect(() => {
        let mounted = true;
        api.get(`/vendedores/${vid}`)
            .then(({ data }) => { if (mounted) setState({ loading: false, data, error: null }); })
            .catch((e) => { if (mounted) setState({ loading: false, data: null, error: e.response?.status === 404 ? "notfound" : "error" }); });
        return () => { mounted = false; };
    }, [vid]);

    if (state.loading) return <div className="p-10 text-center text-[var(--tb-muted)]">{t("Cargando vendedor…", "Loading seller…")}</div>;
    if (state.error === "notfound") return (
        <div className="max-w-xl mx-auto p-10 text-center">
            <h1 className="font-display text-2xl mb-2">{t("Vendedor no encontrado", "Seller not found")}</h1>
            <p className="text-[var(--tb-text-2)] mb-4">{t("Este vendedor no existe o ya no está activo.", "This seller doesn't exist or is no longer active.")}</p>
            <Link to="/tienda" className="tb-btn-primary inline-flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> {t("Ver catálogo", "Browse catalog")}</Link>
        </div>
    );
    if (state.error) return <div className="p-10 text-center text-rose-400">{t("Ocurrió un error. Intenta de nuevo.", "An error occurred. Please try again.")}</div>;

    const { vendor, products } = state.data;
    const social = vendor.social_links || {};

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="vendor-public-page">
            <Link to="/tienda" className="inline-flex items-center gap-1 text-xs text-[var(--tb-muted)] hover:text-[var(--tb-gold)]">
                <ArrowLeft className="w-3.5 h-3.5" /> {t("Volver al catálogo", "Back to catalog")}
            </Link>

            <div className="tb-card p-5 md:p-6 flex flex-col md:flex-row gap-5 items-center md:items-start">
                {vendor.foto ? (
                    <img src={vendor.foto} alt={vendor.display_name} className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover ring-2 ring-[var(--tb-gold)]/40" />
                ) : (
                    <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-[var(--tb-bg-2)] flex items-center justify-center text-[var(--tb-muted)] font-display text-2xl">
                        {vendor.display_name?.[0]?.toUpperCase() || "V"}
                    </div>
                )}
                <div className="flex-1 min-w-0 text-center md:text-left">
                    <div className="tb-overline text-[var(--tb-gold)]">{t("VENDEDOR TERRA", "TERRA SELLER")}</div>
                    <h1 className="font-display text-2xl md:text-4xl inline-flex items-center gap-2 flex-wrap justify-center md:justify-start">
                        {vendor.display_name}
                        {vendor.is_verified && <BadgeCheck className="w-5 h-5 md:w-6 md:h-6 text-[var(--tb-gold)]" aria-label={t("Verificado", "Verified")} />}
                    </h1>
                    {vendor.categoria && (
                        <div className="text-xs text-[var(--tb-text-2)] mt-1">{vendor.categoria}</div>
                    )}
                    {vendor.bio && (
                        <p className="text-sm text-[var(--tb-text-2)] mt-3 whitespace-pre-line max-w-2xl">{vendor.bio}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-4 justify-center md:justify-start">
                        {social.instagram && <SocialLink href={social.instagram} icon={Instagram} label="Instagram" />}
                        {social.facebook && <SocialLink href={social.facebook} icon={Facebook} label="Facebook" />}
                        {social.tiktok && <SocialLink href={social.tiktok} icon={ExternalLink} label="TikTok" />}
                        {social.whatsapp && <SocialLink href={waLink(social.whatsapp)} icon={MessageCircle} label="WhatsApp" />}
                    </div>
                </div>
            </div>

            <div>
                <div className="flex items-baseline justify-between mb-3">
                    <h2 className="font-display text-xl md:text-2xl">{t("Productos", "Products")} ({products.length})</h2>
                </div>
                {products.length === 0 ? (
                    <div className="tb-card p-8 text-center text-[var(--tb-text-2)]" data-testid="vendor-public-empty">
                        {t("Este vendedor aún no tiene productos publicados.", "This seller has no published products yet.")}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5" data-testid="vendor-public-products">
                        {products.map((p) => <ProductCard key={p.sku} p={p} />)}
                    </div>
                )}
            </div>
        </div>
    );
}

function SocialLink({ href, icon: Icon, label }) {
    return (
        <a href={href} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[var(--tb-border)] hover:border-[var(--tb-gold)] hover:text-[var(--tb-gold)] transition-colors"
            data-testid={`vendor-social-${label.toLowerCase()}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
        </a>
    );
}

function waLink(v) {
    if (!v) return "#";
    if (v.startsWith("http")) return v;
    const digits = v.replace(/[^0-9]/g, "");
    return `https://wa.me/${digits}`;
}
